import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAuthToken } from "../../../utils/getAuthToken";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Debouncing for API calls to prevent spam
const debounceMap = new Map();
const DEBOUNCE_TIME = 3000; // 3 seconds for dashboard calls

const shouldDebounce = (key) => {
  const now = Date.now();
  const lastCall = debounceMap.get(key);
  
  if (lastCall && (now - lastCall) < DEBOUNCE_TIME) {
    return true; // Should debounce (skip this call)
  }
  
  debounceMap.set(key, now);
  return false; // Don't debounce (allow this call)
};

// Clears the module-level debounce map — call on logout so the next
// account's first fetch is never silently skipped as "too soon".
export const __resetDashboardCaches = () => {
  debounceMap.clear();
};

// Async thunk for fetching account details
export const fetchAccountDetails = createAsyncThunk(
  "dashboard/fetchAccountDetails",
  async (_, { rejectWithValue }) => {
    if (shouldDebounce('fetchAccountDetails')) {
      return rejectWithValue("Debounced");
    }

    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/auth/account`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch account details");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Owner role only: update account display name (general account details)
export const updateAccountName = createAsyncThunk(
  "dashboard/updateAccountName",
  async (Name, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(`${SERVER_DOMAIN}/api/auth/account/name`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ Name }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || "Failed to update account name");
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update account name");
    }
  }
);

// Async thunk for fetching contact status counts
export const fetchContactStatusCounts = createAsyncThunk(
  "dashboard/fetchContactStatusCounts",
  async ({ startTime, endTime }, { rejectWithValue, getState }) => {
    // Debounce check
    if (shouldDebounce(`fetchContactStatusCounts_${startTime}_${endTime}`)) {
      console.log('🚫 Debounced fetchContactStatusCounts call');
      return rejectWithValue("Debounced");
    }

    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    // Get workspaceId from Redux state (not localStorage for security)
    const workspaceId = getState()?.auth?.user?.workspaceId || getState()?.workspace?.currentWorkspace?._id;

    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/contacts/status-count`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ startTime, endTime, workspaceId }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch contact status counts");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching message counts
export const fetchMessageCounts = createAsyncThunk(
  "dashboard/fetchMessageCounts",
  async ({ startTime, endTime }, { rejectWithValue, getState }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    
    // Get workspaceId from localStorage or state (not stored in memory)
    // Get workspaceId from Redux state (not localStorage for security)
    const workspaceId = getState()?.auth?.user?.workspaceId || getState()?.workspace?.currentWorkspace?._id;

    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/messages/count-messages`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ startTime, endTime, workspaceId }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch message counts");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching team member contact counts
export const fetchTeamMemberContactCounts = createAsyncThunk(
  "dashboard/fetchTeamMemberContactCounts",
  async ({ startTime, endTime }, { rejectWithValue, getState }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    
    // Get workspaceId from localStorage or state (not stored in memory)
    // Get workspaceId from Redux state (not localStorage for security)
    const workspaceId = getState()?.auth?.user?.workspaceId || getState()?.workspace?.currentWorkspace?._id;

    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/teammembers/contacts-count`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ startTime, endTime, workspaceId }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch team member contact counts");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
// Async thunk for fetching contact assigned count
export const fetchContactAssignedCount = createAsyncThunk(
  "dashboard/fetchContactAssignedCount",
  async ({ startTime, endTime }, { rejectWithValue, getState }) => {
    // Debounce check
    if (shouldDebounce(`fetchContactAssignedCount_${startTime}_${endTime}`)) {
      console.log('🚫 Debounced fetchContactAssignedCount call');
      return rejectWithValue("Debounced");
    }

    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    
    // Get workspaceId from localStorage or state (not stored in memory)
    // Get workspaceId from Redux state (not localStorage for security)
    const workspaceId = getState()?.auth?.user?.workspaceId || getState()?.workspace?.currentWorkspace?._id;

    if (!token) {
      return rejectWithValue("No token found");
    }

  try {
    const response = await fetch(
      `${SERVER_DOMAIN}/api/reports/getassignedcount`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ startTime, endTime, workspaceId }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch contact assigned count");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    return rejectWithValue(error.message);
  }
  }
);
// Async thunk for fetching contact closed count
export const fetchContactClosedCount = createAsyncThunk(
  "dashboard/fetchContactClosedCount",
  async (body, { rejectWithValue, getState }) => {
    // Debounce check
    const bodyKey = JSON.stringify(body);
    if (shouldDebounce(`fetchContactClosedCount_${bodyKey}`)) {
      console.log('🚫 Debounced fetchContactClosedCount call');
      return rejectWithValue("Debounced");
    }

    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    // Get workspaceId from Redux state (not localStorage for security)
    const workspaceIdFromState = getState()?.auth?.user?.workspaceId || getState()?.workspace?.currentWorkspace?._id;
    const workspaceIdFromLocalStorage = null; // Removed - use Redux state instead
    const workspaceId = workspaceIdFromState || workspaceIdFromLocalStorage;

    console.log('fetchContactClosedCount - workspaceId sources:', {
      fromLocalStorage: workspaceIdFromLocalStorage,
      fromState: workspaceIdFromState,
      final: workspaceId,
      body
    });

    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/reports/getclosedcount`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ...body, workspaceId }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch contact closed count");
      }

      const data = await response.json();
      console.log('fetchContactClosedCount - API Response:', data);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching contact overview
export const fetchContactOverview = createAsyncThunk(
  "dashboard/fetchContactOverview",
  async (body, { rejectWithValue, getState }) => {
    // Debounce check
    if (shouldDebounce(`fetchContactOverview_${JSON.stringify(body)}`)) {
      console.log('🚫 Debounced fetchContactOverview call');
      return rejectWithValue("Debounced");
    }

    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    
    // Get workspaceId from localStorage or state (not stored in memory)
    // Get workspaceId from Redux state (not localStorage for security)
    const workspaceId = getState()?.auth?.user?.workspaceId || getState()?.workspace?.currentWorkspace?._id;

    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/reports/getcontactoverview`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ...body, workspaceId }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch contact overview");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching the Contacts Overview card summary (Engaged/Closed/Created for a single range)
export const fetchContactsOverviewCard = createAsyncThunk(
  "dashboard/fetchContactsOverviewCard",
  // No `range` anymore (2026-08-18) -- the card now shows a live contact-status
  // snapshot (Assigned/Unassigned/Closed), which doesn't have a meaningful date
  // range the way the old Engaged/Created/Closed metrics did. Single debounce
  // key since there's only ever one shape of this request now.
  async (_, { rejectWithValue }) => {
    // Debounce check
    if (shouldDebounce('fetchContactsOverviewCard')) {
      console.log('🚫 Debounced fetchContactsOverviewCard call');
      return rejectWithValue("Debounced");
    }

    // Get token from memory (not localStorage)
    const token = await getAuthToken();

    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/reports/getcontactsoverviewcard`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch contacts overview card data");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching the Dashboard "Unattended" card stats (count of conversations
// waiting on a reply + oldest waiting timestamp)
export const fetchUnattendedStats = createAsyncThunk(
  "dashboard/fetchUnattendedStats",
  async (_, { rejectWithValue, getState }) => {
    // Debounce check
    if (shouldDebounce('fetchUnattendedStats')) {
      return rejectWithValue("Debounced");
    }

    // Get token from memory (not localStorage)
    const token = await getAuthToken();

    // Get workspaceId from Redux state (not localStorage for security)
    const workspaceId = getState()?.auth?.user?.workspaceId || getState()?.workspace?.currentWorkspace?._id;

    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/reports/getunattendedstats`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ workspaceId }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch unattended stats");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    account: null,
    contactStatusCounts: null,
    messageCounts: null,
    teamMemberContactCounts: null, // New state for team member contact counts
    loading: {
      overview: false,
      closedCount: false,
      assignedCount: false,
      account: false,
      contactStatusCounts: false,
      messageCounts: false,
      teamMemberContactCounts: false,
      contactsOverviewCard: false,
      unattendedStats: false,
    },
    error: {
      overview: null,
      closedCount: null,
      assignedCount: null,
      account: null,
      contactStatusCounts: null,
      messageCounts: null,
      teamMemberContactCounts: null,
      contactsOverviewCard: null,
      unattendedStats: null,
    },
    contactOverviewCount: null,
    contactsOverviewCard: null,
    unattendedStats: null,
  },
  reducers: {
    resetError: (state) => {
      state.error = {
        overview: null,
        closedCount: null,
        assignedCount: null,
        teamMemberSuccess: null,
        account: null,
        contactStatusCounts: null,
        messageCounts: null,
        teamMemberContactCounts: null,
        contactsOverviewCard: null,
        unattendedStats: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchAccountDetails
      .addCase(fetchAccountDetails.pending, (state) => {
        state.loading.account = true;
        state.error.account = null;
      })
      .addCase(fetchAccountDetails.fulfilled, (state, action) => {
        state.loading.account = false;
        state.account = action.payload;
        state.error.account = null;
      })
      .addCase(fetchAccountDetails.rejected, (state, action) => {
        state.loading.account = false;
        if (action.payload !== "Debounced") {
          state.account = null;
          state.error.account = action.payload;
        }
      })
      .addCase(updateAccountName.pending, (state) => {
        state.loading.account = true;
        state.error.account = null;
      })
      .addCase(updateAccountName.fulfilled, (state, action) => {
        state.loading.account = false;
        state.account = action.payload;
        state.error.account = null;
      })
      .addCase(updateAccountName.rejected, (state, action) => {
        state.loading.account = false;
        state.error.account = action.payload;
      })
      // Handle fetchContactStatusCounts
      .addCase(fetchContactStatusCounts.pending, (state) => {
        state.loading.contactStatusCounts = true;
        state.error.contactStatusCounts = null;
      })
      .addCase(fetchContactStatusCounts.fulfilled, (state, action) => {
        state.loading.contactStatusCounts = false;
        state.contactStatusCounts = action.payload;
        state.error.contactStatusCounts = null;
      })
      .addCase(fetchContactStatusCounts.rejected, (state, action) => {
        state.loading.contactStatusCounts = false;
        if (action.payload !== "Debounced") {
          state.contactStatusCounts = null;
          state.error.contactStatusCounts = action.payload;
        }
      })
      // Handle fetchMessageCounts
      .addCase(fetchMessageCounts.pending, (state) => {
        state.loading.messageCounts = true;
        state.error.messageCounts = null;
      })
      .addCase(fetchMessageCounts.fulfilled, (state, action) => {
        state.loading.messageCounts = false;
        state.messageCounts = action.payload;
        state.error.messageCounts = null;
      })
      .addCase(fetchMessageCounts.rejected, (state, action) => {
        state.loading.messageCounts = false;
        state.messageCounts = null;
        state.error.messageCounts = action.payload;
      })
      // Handle fetchTeamMemberContactCounts
      .addCase(fetchTeamMemberContactCounts.pending, (state) => {
        state.loading.teamMemberContactCounts = true;
        state.error.teamMemberContactCounts = null;
      })
      .addCase(fetchTeamMemberContactCounts.fulfilled, (state, action) => {
        state.loading.teamMemberContactCounts = false;
        state.teamMemberContactCounts = action.payload;
        state.error.teamMemberContactCounts = null;
      })
      .addCase(fetchTeamMemberContactCounts.rejected, (state, action) => {
        state.loading.teamMemberContactCounts = false;
        state.teamMemberContactCounts = null;
        state.error.teamMemberContactCounts = action.payload;
      })
      // Handle fetchContactOverview
      .addCase(fetchContactOverview.pending, (state) => {
        state.loading.overview = true;
        state.error.overview = null;
      })
      .addCase(fetchContactOverview.fulfilled, (state, action) => {
        state.loading.overview = false;
        state.contactOverviewCount = action.payload;
        state.error.overview = null;
      })
      .addCase(fetchContactOverview.rejected, (state, action) => {
        state.loading.overview = false;
        if (action.payload !== "Debounced") {
          state.contactOverviewCount = null;
          state.error.overview = action.payload;
        }
      })
      // Handle fetchContactClosedCount
      .addCase(fetchContactClosedCount.pending, (state) => {
        state.loading.closedCount = true;
        state.error.closedCount = null;
      })
      .addCase(fetchContactClosedCount.fulfilled, (state, action) => {
        state.loading.closedCount = false;
        state.contactClosedCount = action.payload;
        state.error.closedCount = null;
      })
      .addCase(fetchContactClosedCount.rejected, (state, action) => {
        state.loading.closedCount = false;
        if (action.payload !== "Debounced") {
          state.contactClosedCount = null;
          state.error.closedCount = action.payload;
        }
      })
      // Handle fetchContactAssignedCount
      .addCase(fetchContactAssignedCount.pending, (state) => {
        state.loading.assignedCount = true;
        state.error.assignedCount = null;
      })
      .addCase(fetchContactAssignedCount.fulfilled, (state, action) => {
        state.loading.assignedCount = false;
        state.contactAssignedCount = action.payload;
        state.error.assignedCount = null;
      })
      .addCase(fetchContactAssignedCount.rejected, (state, action) => {
        state.loading.assignedCount = false;
        if (action.payload !== "Debounced") {
          state.contactAssignedCount = null;
          state.error.assignedCount = action.payload;
        }
      })
      // Handle fetchContactsOverviewCard
      .addCase(fetchContactsOverviewCard.pending, (state) => {
        state.loading.contactsOverviewCard = true;
        state.error.contactsOverviewCard = null;
      })
      .addCase(fetchContactsOverviewCard.fulfilled, (state, action) => {
        state.loading.contactsOverviewCard = false;
        state.contactsOverviewCard = action.payload;
        state.error.contactsOverviewCard = null;
      })
      .addCase(fetchContactsOverviewCard.rejected, (state, action) => {
        state.loading.contactsOverviewCard = false;
        if (action.payload !== "Debounced") {
          state.contactsOverviewCard = null;
          state.error.contactsOverviewCard = action.payload;
        }
      })
      // Handle fetchUnattendedStats
      .addCase(fetchUnattendedStats.pending, (state) => {
        state.loading.unattendedStats = true;
        state.error.unattendedStats = null;
      })
      .addCase(fetchUnattendedStats.fulfilled, (state, action) => {
        state.loading.unattendedStats = false;
        state.unattendedStats = action.payload;
        state.error.unattendedStats = null;
      })
      .addCase(fetchUnattendedStats.rejected, (state, action) => {
        state.loading.unattendedStats = false;
        if (action.payload !== "Debounced") {
          state.unattendedStats = null;
          state.error.unattendedStats = action.payload;
        }
      });
  },
});

// Selector to calculate total closed count
export const selectTotalContactCounts = (state) => {
  const teamMemberContactCounts = state.dashboard.teamMemberContactCounts;
  if (!teamMemberContactCounts || !Array.isArray(teamMemberContactCounts)) {
    return { totalClosed: 0 };
  }

  const totalClosed = teamMemberContactCounts.reduce(
    (total, member) => total + (member.closedCount || 0),
    0
  );

  return { totalClosed };
};

export const { resetError } = dashboardSlice.actions;
export default dashboardSlice.reducer;
